import { useCallback, useState } from 'react';

/**
 * § 401 — a control that becomes disabled WHILE it holds focus keeps focus.
 *
 * By the HTML focus-fixup rule, when the focused element stops being focusable (natively
 * `disabled`), focus moves to `<body>` at the next rendering update. § 330 measured it in
 * Chromium. So a control that disables ITSELF in response to its own activation throws a
 * keyboard user out of their place: a page button disabled for the length of the fetch it
 * started, a Next that has just reached the last page, an expand chevron while its row loads,
 * a page-size Dropdown while the page refetches.
 *
 * The rule: such a control is natively `disabled` only while it does NOT hold focus. While it
 * does, it renders `aria-disabled` instead. It is still announced as unavailable and still
 * refuses activation (its own handler checks), but it stays focusable, so focus stays where the
 * user put it. The moment focus leaves, the native attribute is back. A disabled control nobody
 * is standing on renders exactly what it always did.
 *
 * (Button made a different choice in § 335: `focusableWhenDisabled` keeps it focusable at all
 * times, opt-in, because a Save has to stay reachable for the whole save.)
 */

/**
 * Which of a component's controls holds focus right now, by a key the component chooses — a
 * page number, `'trigger'`. `bind(key)` gives the control the two handlers that keep it current.
 */
export function useFocusHolder<K extends string = string>(): {
  holder: K | null;
  bind: (key: K) => { onFocus: () => void; onBlur: () => void };
} {
  const [holder, setHolder] = useState<K | null>(null);
  const bind = useCallback((key: K) => ({
    onFocus: () => setHolder(key),
    /* Only clear our own key: when focus moves between two bound controls, the blur of the one
       must not erase the focus of the other, whichever order the updates land in. */
    onBlur: () => setHolder((current) => (current === key ? null : current)),
  }), []);
  return { holder, bind };
}

/**
 * The disabled attributes for a control that may be disabled while it holds focus: native
 * `disabled` when it does not, `aria-disabled` when it does. The control's own activation
 * handler must still refuse while `disabled` — `aria-disabled` blocks nothing by itself.
 */
export const retainingDisabled = (
  disabled: boolean,
  holdsFocus: boolean,
): { disabled: boolean; 'aria-disabled': true | undefined } => ({
  disabled: disabled && !holdsFocus,
  'aria-disabled': disabled && holdsFocus ? true : undefined,
});
